import React from "react";
import Button from "../../Button/Button";
import Typography from "../../Typography/Typography";
import "./stepper.scss";

const Stepper = ({
    steps = [],
    currentStep = 1,
    onNext,
    onPrevious,
    onComplete,
    showProgress = true,
    showNavigation = true,
    className = "",
    stepClassName = "",
    contentClassName = "",
    progressClassName = "",
    nextButtonText = "Next",
    completeButtonText = "Complete",
    isProcessing = false,
    disablePrevious = false,
    disableNext = false,
    onStepChange,
}) => {
    const progress =
        steps.length > 1 ? ((currentStep - 1) / (steps.length - 1)) * 100 : 0;
    const isLastStep = currentStep === steps.length;

    const handleNextOrComplete = async () => {
        if (isLastStep) {
            onComplete && onComplete();
        } else {
            onNext && (await onNext());
        }
    };

    return (
        <div className={`stepper-container ${className}`}>
            {/* Steps Header */}
            <div className={`stepper-header ${stepClassName}`}>
                <div className="d-flex justify-content-between position-relative mb-4">
                    {steps.map((step, index) => {
                        const stepNumber = index + 1;
                        const isActive = stepNumber === currentStep;
                        const isCompleted = stepNumber < currentStep;

                        return (
                            <div
                                key={index}
                                className="text-center position-relative"
                                style={{ flex: 1 }}
                            >
                                {/* Step Circle */}
                                <div
                                    className={`step-circle rounded-circle d-inline-flex align-items-center justify-content-center ${
                                        isCompleted
                                            ? "step-completed"
                                            : isActive
                                              ? "step-active"
                                              : "step-upcoming"
                                    }`}
                                >
                                    {isCompleted ? (
                                        <i className="bi bi-check-lg"></i>
                                    ) : (
                                        <span>{stepNumber}</span>
                                    )}
                                </div>

                                {/* Step Title */}
                                <Typography
                                    variant="h6"
                                    className={`step-title mt-3 ${
                                        isActive
                                            ? "stepper-text-primary"
                                            : isCompleted
                                              ? "text-successful"
                                              : "text-muted"
                                    }`}
                                >
                                    {step.title}
                                </Typography>

                                {/* Step Subtitle */}
                                <Typography
                                    variant="body2"
                                    className="step-description text-muted mt-1"
                                >
                                    {step.subtitle}
                                </Typography>

                                {/* Connecting Line */}
                                {index < steps.length - 1 && (
                                    <div
                                        className={`step-line position-absolute ${
                                            isCompleted ? "completed" : ""
                                        }`}
                                    ></div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Progress Bar - Now below the steps */}
                {showProgress && (
                    <div className={`stepper-progress ${progressClassName}`}>
                        <div className="progress mb-4">
                            <div
                                className="progress-bar"
                                role="progressbar"
                                style={{ width: `${progress}%` }}
                                aria-valuenow={progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>
                        </div>
                    </div>
                )}
            </div>

            {/* Step Content */}
            <div className={`stepper-content ${contentClassName}`}>
                <div className="bg-white p-5 rounded-4 shadow-sm border">
                    {steps[currentStep - 1]?.content || (
                        <div className="text-center text-muted">
                            <i className="bi bi-info-circle fs-1 mb-3"></i>
                            <Typography variant="body1">
                                No content available for this step
                            </Typography>
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation Buttons */}
            {showNavigation && (
                <div className="d-flex justify-content-between mt-4">
                    {currentStep > 1 && currentStep !== steps.length &&(
                        <Button
                            variant="secondary"
                            onClick={onPrevious}
                            disabled={disablePrevious}
                            className="px-4 py-2"
                            fullWidth={false}
                        >
                            <i className="bi bi-arrow-left me-2"></i>
                            Previous
                        </Button>
                    )}
                    {!isLastStep && (
                        <Button
                            variant="primary"
                            onClick={handleNextOrComplete}
                            disabled={isProcessing || disableNext}
                            className="px-4 py-2 ms-auto"
                            fullWidth={false}
                        >
                            {isProcessing ? (
                                <>
                                    <i className="bi bi-arrow-clockwise me-2 spin"></i>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    {nextButtonText}
                                    <i className="bi bi-arrow-right ms-2"></i>
                                </>
                            )}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default Stepper;
